import { Fragment } from "react";
import { createStyles, Title, Text, Button, rem } from "@mantine/core";
import { Link } from "react-router-dom";
import { AnimatedBackground } from "./AnimatedBackground";
import PageContainer from "./layout/PageContainer";
import { homePrimaryButton, homeOutlineOnDarkButton } from "./homeButtonStyles";

const HERO_KEYFRAMES = `
  @keyframes heroSlideUp {
    from {
      opacity: 0;
      transform: translateY(32px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const useStyles = createStyles((theme) => {
  const enter = (delay) => ({
    opacity: 0,
    animation: `heroSlideUp 0.85s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms forwards`,
    "@media (prefers-reduced-motion: reduce)": {
      opacity: 1,
      animation: "none",
    },
  });

  return {
    hero: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      minHeight: rem(520),
      width: "100%",
      [theme.fn.smallerThan("md")]: {
        minHeight: rem(440),
      },
    },
    inner: {
      paddingTop: rem(36),
      paddingBottom: rem(44),
      position: "relative",
      zIndex: 1,
    },
    copy: {
      maxWidth: rem(640),
    },
    eyebrow: {
      ...enter(0),
      color: "rgba(255,255,255,0.85)",
      fontSize: theme.fontSizes.xs,
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      marginBottom: theme.spacing.md,
    },
    title: {
      ...enter(120),
      fontFamily: theme.headings?.fontFamily || '"Noto Serif TC", serif',
      color: "#fff",
      fontSize: "clamp(1.75rem, 4vw, 2.75rem)",
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: "normal",
      textShadow: "0 2px 20px rgba(0,0,0,0.35)",
    },
    description: {
      ...enter(260),
      color: "rgba(255,255,255,0.92)",
      fontSize: "clamp(0.95rem, 1.3vw, 1.05rem)",
      lineHeight: 1.8,
      marginTop: theme.spacing.lg,
      maxWidth: 460,
      letterSpacing: "normal",
    },
    highlights: {
      ...enter(380),
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: rem(10),
      margin: `${theme.spacing.lg}px 0 0`,
      padding: 0,
      listStyle: "none",
    },
    highlightPill: {
      margin: 0,
      display: "inline-block",
      padding: `${rem(8)} ${rem(14)}`,
      borderRadius: theme.radius.xl,
      backgroundColor: "rgba(255,255,255,0.14)",
      border: "1px solid rgba(255,255,255,0.28)",
      color: "rgba(255,255,255,0.95)",
      fontSize: theme.fontSizes.sm,
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: "normal",
      textAlign: "center",
      whiteSpace: "nowrap",
      backdropFilter: "blur(6px)",
    },
    highlightSep: {
      margin: `0 ${rem(6)}`,
      opacity: 0.75,
    },
    ctaGroup: {
      ...enter(500),
      display: "flex",
      gap: theme.spacing.sm,
      marginTop: theme.spacing.lg,
      flexWrap: "wrap",
    },
    primaryBtn: homePrimaryButton(theme),
    outlineBtn: homeOutlineOnDarkButton(theme),
  };
});

const heroHighlightGroups = [
  ["品質穩定", "產地直供"],
  ["每週空運到港"],
  ["批發零售", "餐飲供應"],
];

export function HeroContentLeft() {
  const { classes } = useStyles();

  return (
    <AnimatedBackground>
      <style>{HERO_KEYFRAMES}</style>
      <div className={classes.hero}>
        <PageContainer>
          <div className={classes.inner}>
            <div className={classes.copy}>
              <Text className={classes.eyebrow}>馬來西亞彭亨州 · 貓山王朝</Text>

              <Title order={1} className={classes.title}>
                頂級貓山王榴槤
                <br />
                每週空運到港
              </Title>

              <Text className={classes.description}>
                鮮果、急凍產品 — 香港食肆、餐廳、甜品店及零售客戶的可靠供應夥伴。
              </Text>

              <ul className={classes.highlights}>
                {heroHighlightGroups.map((parts) => (
                  <li key={parts.join("-")} className={classes.highlightPill}>
                    {parts.map((part, index) => (
                      <Fragment key={part}>
                        {index > 0 && <span className={classes.highlightSep}>·</span>}
                        {part}
                      </Fragment>
                    ))}
                  </li>
                ))}
              </ul>

              <div className={classes.ctaGroup}>
                <Link to="/product" style={{ textDecoration: "none" }}>
                  <Button className={classes.primaryBtn} size="md" radius="md">
                    瀏覽產品
                  </Button>
                </Link>
                <Link to="/contact" style={{ textDecoration: "none" }}>
                  <Button className={classes.outlineBtn} size="md" radius="md" variant="outline">
                    查詢訂銷
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </PageContainer>
      </div>
    </AnimatedBackground>
  );
}
